import { sendGAEvent } from '@next/third-parties/google';
import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { useCallback } from 'react';
import { BaseLineExclamation } from '@/app/components/icons/BaseLineExclamation';
import { PlanDialogScene } from '@/app/components/shared/PlanDetailDialog';
import { SITE } from '@/app/constants';
import { useGlobalModal, useWatermarkConfirmation } from '@/app/hooks';
import { cn, reportEvent } from '@/app/utils';

interface DownloadWatermarkOptionProps {
  isFree: boolean;
  hasCopyright?: boolean; // 是否有版权限制，有版权限制时不允许去水印
  withWatermark?: boolean;
  setWithWatermark?: (withWatermark: boolean) => void;
  /** 创作 ID（去水印下载场景需要） */
  creationId?: string;
  /** 下载的类型  creation: 生成物；frame: 抽帧*/
  type?: 'creation' | 'frame';
}

export const DownloadWatermarkOption: FC<DownloadWatermarkOptionProps> = ({
  isFree,
  hasCopyright = false,
  withWatermark,
  setWithWatermark,
  creationId,
  type = 'creation',
}) => {
  const t = useTranslations();
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const { handleNoWatermarkAction } = useWatermarkConfirmation();

  const handleOptionClick = useCallback(
    (value: boolean, e: React.MouseEvent) => {
      e.preventDefault();

      // 免费用户点击不带水印下载，弹出付费弹窗
      if (isFree && !value) {
        openPlanDetailDialog({ scene: PlanDialogScene.Download, creationId, fromPage: 'download tooltip' });
        reportEvent('vip_price_modal_exposure', {
          from_page: 'download tooltip',
        });
        sendGAEvent('event', 'subscription_exposure', {
          from_page: 'download tooltip',
        });
        return; // 不能继续执行
      }

      // 点击不带水印下载，调用 handleNoWatermarkAction 判断是否需要弹窗
      if (!value) {
        handleNoWatermarkAction();
      }

      setWithWatermark?.(value);
    },
    [isFree, hasCopyright],
  );

  // 如果 value 为 true，则表示带水印按钮，否则表示无水印按钮
  const renderOption = (value: boolean, label: string) => {
    const isSelected = withWatermark === value;
    const isDisabled = !value && hasCopyright; // 有版权限制时禁用去水印选项
    const content = (
      <div
        className={cn(
          'flex items-center rounded-4 px-3 py-1.5',
          isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-system-hover01',
          isSelected && !isDisabled && 'bg-system-hover01',
        )}
        onClick={(e) => !isDisabled && handleOptionClick(value, e)}
      >
        <div className="mr-2 flex size-4 shrink-0 items-center justify-center rounded-full border border-system-white64">
          {isSelected && <div className="size-2 rounded-full bg-current" />}
        </div>
        <div className="flex flex-col">
          <span className="whitespace-nowrap">{label}</span>
          {isDisabled && (
            <span className="mt-0.5 text-system-text03 text-xs">
              {t('sharedComponent.downloadButton.copyrightRestriction')}
            </span>
          )}
        </div>
        {isFree && !value && !isDisabled && (
          <span className="ml-2 flex items-center gap-1 rounded-[100px] bg-system-blue02 px-2 py-0.5 text-white text-xs">
            {t('create.createForm.badge.upgrade')}
            <BaseLineExclamation className="shrink-0 text-sm" />
          </span>
        )}
      </div>
    );
    return content;
  };

  const twoOptions = (
    <>
      {renderOption(true, t('sharedComponent.downloadButton.withWatermark'))}
      {renderOption(false, t('sharedComponent.downloadButton.withoutWatermark'))}
    </>
  );

  const onlyDownloadTip = (
    <div className={cn('flex cursor-pointer items-center rounded-4 px-3 py-1.5 hover:bg-system-hover01')}>
      <span className="whitespace-nowrap">{t('sharedComponent.downloadButton.download')}</span>
    </div>
  );

  // 对于生成物
  if (type === 'creation') {
    // 国内免费用户需要合规风险，其他情况都需要展示选择多选项
    if (SITE === 'china' && isFree) {
      return onlyDownloadTip;
    }
    return twoOptions;
  }

  // 对于抽帧
  if (SITE === 'global') {
    // 海外免费用户和有版权的作品，需要展示选择多选项
    if (isFree || hasCopyright) {
      return twoOptions;
    }
    return onlyDownloadTip;
  }

  // 国内只有有版权的作品，需要展示选择多选项，此处是为了合规要求
  if (hasCopyright) {
    return twoOptions;
  }

  return onlyDownloadTip;
};
